"use client";

import clsx from "clsx";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import Button from "@/components/library/Button";
import Icon, {ICON_SIZES} from "@/components/library/Icon";
import {useOrg} from "@/hooks/useOrg";
import {formatPhoneNumber} from "@/utils/library/formatters";
import {OnboardingStepHeader} from "./OnboardingStepHeader";

interface SetupAndTryGailStepProps {
   readonly onBack?: () => void;
   readonly onNext: () => void;
   readonly isLastStep?: boolean;
}

export function SetupAndTryGailStep({onBack, onNext, isLastStep = false}: SetupAndTryGailStepProps) {
   const {t} = useTranslation("onboarding");
   const {org} = useOrg();

   const existingPhoneNumber = org?.services?.twilioPhoneNumber ?? null;
   const [phoneNumber] = useState<string | null>(existingPhoneNumber);

   return (
      <div className="flex flex-col h-full">
         <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center">
            <div className="w-full max-w-lg space-y-8">
               <OnboardingStepHeader
                  title={t("callReceptionist.headline")}
                  description={t("callReceptionist.description")}
               />

               <div className="space-y-6">
                  {phoneNumber ? (
                     <>
                        <div className="rounded-3xl border border-emerald-500/70 bg-emerald-500/5 p-8 space-y-6">
                           <div className="text-center space-y-2">
                              <p className="text-sm font-semibold uppercase tracking-wide text-emerald-700 dark:text-emerald-300">
                                 {t("callReceptionist.assignedNumberLabel")}
                              </p>
                              <p className="text-4xl font-bold tracking-wide text-foreground">
                                 {formatPhoneNumber(phoneNumber)}
                              </p>
                           </div>

                           <div className="flex items-start gap-3 rounded-xl bg-emerald-500/10 p-4">
                              <Icon
                                 icon="phone"
                                 size={ICON_SIZES.SM}
                                 className="text-emerald-600 dark:text-emerald-400 mt-0.5 shrink-0"
                              />
                              <p className="text-sm text-foreground">
                                 {t("callReceptionist.tryItPrompt")}
                              </p>
                           </div>
                        </div>

                        <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 p-4">
                           <Icon
                              icon="circle-info"
                              size={ICON_SIZES.SM}
                              className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0"
                           />
                           <p className="text-sm text-amber-800 dark:text-amber-300">
                              {t("callReceptionist.forwardingNote")}
                           </p>
                        </div>
                     </>
                  ) : (
                     <div className="rounded-3xl border border-amber-500/70 bg-amber-500/5 p-8 space-y-4 text-center">
                        <div className="flex justify-center">
                           <Icon
                              icon="clock"
                              size={ICON_SIZES.XL}
                              className="text-amber-600 dark:text-amber-400"
                           />
                        </div>
                        <h3 className="text-xl font-semibold text-foreground">
                           {t("callReceptionist.noNumberTitle")}
                        </h3>
                        <p className="text-muted-foreground">
                           {t("callReceptionist.noNumberDescription")}
                        </p>
                     </div>
                  )}
               </div>
            </div>
         </div>

         <div
            className={clsx(
               "sticky bottom-0 flex items-center gap-4 px-8 py-4",
               "bg-panel_secondary/50 border-t border-200 backdrop-blur-md"
            )}
         >
            {onBack && (
               <Button type="tertiary" className="min-w-[120px]" onClick={onBack}>
                  {t("wizard.back")}
               </Button>
            )}
            <Button type="primary" className="min-w-[120px] ml-auto" onClick={onNext}>
               {isLastStep ? t("wizard.done") : t("wizard.next")}
            </Button>
         </div>
      </div>
   );
}
